"use client";

import { c as _c } from "react/compiler-runtime";
import type { PropsWithChildren } from "react";
import Image from "next/image";
import Link from "next/link";
import { notFound } from "next/navigation";
import { ArrowUpRight } from "lucide-react";
import { motion } from "motion/react";
import type { ProjectDetail } from "@/types/projects";
import { shimmer } from "@/lib/shimmer";
import { BreakoutWrapper } from "@/ui/atoms/breakout-wrapper";
import { Button } from "@/ui/atoms/button";
import { TechnologiesCarousel } from "@/ui/post/technologies-carousel";
export function Project(t0) {
  const $ = _c(56);
  const {
    imageUrl,
    slug,
    title,
    description,
    externalLink,
    technologies,
    date,
    children
  } = t0;
  if (!slug) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = notFound();
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0
    };
    t2 = {
      opacity: 1
    };
    t3 = {
      duration: 0.5
    };
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      y: 20,
      opacity: 0
    };
    t5 = {
      y: 0,
      opacity: 1
    };
    t6 = {
      duration: 0.5,
      delay: 0.2
    };
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  const t7 = imageUrl || "/placeholder.svg";
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = shimmer([1200, 630]);
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t7 || $[9] !== t8 || $[10] !== title) {
    t9 = <div className="mb-4 md:mx-auto md:max-w-[50%]"><motion.div initial={t4} animate={t5} transition={t6}><Image src={t7} alt={title} width={1200} height={630} placeholder="blur" blurDataURL={t8} className="w-full object-cover sm:rounded-lg md:max-h-[50vh]" priority={true} /></motion.div></div>;
    $[8] = t7;
    $[9] = t8;
    $[10] = title;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  let t11;
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      y: 20,
      opacity: 0
    };
    t11 = {
      y: 0,
      opacity: 1
    };
    t12 = {
      duration: 0.5,
      delay: 0.4
    };
    $[12] = t10;
    $[13] = t11;
    $[14] = t12;
  } else {
    t10 = $[12];
    t11 = $[13];
    t12 = $[14];
  }
  let t13;
  if ($[15] !== technologies) {
    t13 = <div className="mb-6 md:mx-auto md:max-w-[50%]"><motion.div initial={t10} animate={t11} transition={t12}><TechnologiesCarousel withHashtag={false} technologies={technologies} /></motion.div></div>;
    $[15] = technologies;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== t13 || $[18] !== t9) {
    t14 = <BreakoutWrapper>{t9}{t13}</BreakoutWrapper>;
    $[17] = t13;
    $[18] = t9;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  let t16;
  let t17;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      y: 20,
      opacity: 0
    };
    t16 = {
      y: 0,
      opacity: 1
    };
    t17 = {
      duration: 0.5,
      delay: 0.6
    };
    $[20] = t15;
    $[21] = t16;
    $[22] = t17;
  } else {
    t15 = $[20];
    t16 = $[21];
    t17 = $[22];
  }
  let t18;
  if ($[23] !== title) {
    t18 = <h1 className="font-basis-grotesque-pro mb-4 text-4xl font-bold md:text-5xl">{title}</h1>;
    $[23] = title;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== description) {
    t19 = description && <p className="font-basis-grotesque-pro text-muted-foreground mb-6 text-xl font-normal md:text-2xl">{description}</p>;
    $[25] = description;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== t18 || $[28] !== t19) {
    t20 = <motion.div initial={t15} animate={t16} transition={t17}>{t18}{t19}</motion.div>;
    $[27] = t18;
    $[28] = t19;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  let t22;
  let t23;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      y: 20,
      opacity: 0
    };
    t22 = {
      y: 0,
      opacity: 1
    };
    t23 = {
      duration: 0.5,
      delay: 0.8
    };
    $[30] = t21;
    $[31] = t22;
    $[32] = t23;
  } else {
    t21 = $[30];
    t22 = $[31];
    t23 = $[32];
  }
  let t24;
  if ($[33] !== children) {
    t24 = <motion.div initial={t21} animate={t22} transition={t23} className="prose-sm sm:prose [&>p]:prose-p:text-pretty max-w-none [&>p]:mb-4 lg:[&>p]:mb-8">{children}</motion.div>;
    $[33] = children;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== date) {
    t25 = <p className="text-muted-foreground text-sm">Completed in {date}</p>;
    $[35] = date;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  let t27;
  let t28;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      y: 20,
      opacity: 0
    };
    t27 = {
      y: 0,
      opacity: 1
    };
    t28 = {
      duration: 0.5,
      delay: 0.6
    };
    $[37] = t26;
    $[38] = t27;
    $[39] = t28;
  } else {
    t26 = $[37];
    t27 = $[38];
    t28 = $[39];
  }
  const t29 = `/#${slug}`;
  let t30;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Button variant="outline" className="cursor-pointer" type="button">Back to Projects</Button>;
    $[40] = t30;
  } else {
    t30 = $[40];
  }
  let t31;
  if ($[41] !== t29) {
    t31 = <Link href={t29} scroll={true}>{t30}</Link>;
    $[41] = t29;
    $[42] = t31;
  } else {
    t31 = $[42];
  }
  let t32;
  if ($[43] !== externalLink) {
    t32 = externalLink && <Button asChild={true} variant="outline"><a href={externalLink} target="_blank" rel="noopener noreferrer">Visit Project<ArrowUpRight className="ml-2 h-4 w-4" /></a></Button>;
    $[43] = externalLink;
    $[44] = t32;
  } else {
    t32 = $[44];
  }
  let t33;
  if ($[45] !== t31 || $[46] !== t32) {
    t33 = <motion.div initial={t26} animate={t27} transition={t28} className="mt-8 flex items-center gap-4">{t31}{t32}</motion.div>;
    $[45] = t31;
    $[46] = t32;
    $[47] = t33;
  } else {
    t33 = $[47];
  }
  let t34;
  if ($[48] !== t20 || $[49] !== t24 || $[50] !== t25 || $[51] !== t33) {
    t34 = <div className="mx-auto max-w-[65ch]">{t20}{t24}{t25}{t33}</div>;
    $[48] = t20;
    $[49] = t24;
    $[50] = t25;
    $[51] = t33;
    $[52] = t34;
  } else {
    t34 = $[52];
  }
  let t35;
  if ($[53] !== t14 || $[54] !== t34) {
    t35 = <motion.article initial={t1} animate={t2} transition={t3} className="bg-background text-foreground font-basis-grotesque-pro container mx-auto px-2 py-16 text-sm font-normal sm:text-base md:py-24"><div className="mx-auto max-w-7xl">{t14}{t34}</div></motion.article>;
    $[53] = t14;
    $[54] = t34;
    $[55] = t35;
  } else {
    t35 = $[55];
  }
  return t35;
}